import { useMemo } from "react";
import { useAssertedTenancyContext } from "@bfc-software/app-toolbox";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/aaa-api-client";

import type { QueryLike } from "@bfc-software/bfc-web-ui/lib/async-utils";

/**
 * The selector filter box's roster: AAA app users, not the task planner's.
 *
 * `GET /{orgId}/selectors` is the planner's own roster, and it carries only
 * `external_user_id` - a selector's *name* lives nowhere on that side, because
 * the planner never needed one for anything it does. AAA is the one place a
 * selector's name and their sign-in id sit on the same row, which is exactly
 * what a box searched on "name or id" needs - so this reads
 * `GET /app_user/list` instead, the same call `selectors.tsx`'s `listQuery`
 * makes for the selector-management page.
 *
 * The id this hook hands back is `user_id`, which is the same value the
 * planner reports as `external_user_id` and takes as `selectorAppUserExtIds`
 * on both listings - so picking an option here and writing its id to the
 * `selector` URL param names the identity the whole filter chain keys on, with
 * nothing translated at any layer.
 *
 * `app_name` is `"select"`, matching `selectorAppName` in nextgen_config's
 * selectorsHandler.go and `selectors.tsx`'s own `APP_NAME` - app users are
 * shared across every app on a warehouse, and this is what scopes the list to
 * the ones that actually sign in on the floor.
 */
const APP_NAME = "select";

export interface SelectorOption {
  id: string;
  label: string;
}

/**
 * Org and warehouse come from `useAssertedTenancyContext()`, so this only runs
 * below a warehouse gate. `warehouseId` is also what feeds `warehouseExtId` on the
 * task-planner side - the same value names the same warehouse on both APIs, so
 * there is nothing to translate here either.
 *
 * Read on mount rather than on the dropdown's first open, which is what it used
 * to do. The roster is not only the option list: it is the only place the
 * `selector` URL param's AAA id can be turned back into a name, so a page
 * loaded on a link that already carries one has to have it before anybody
 * touches the control. Deferring it meant the box drew the bare id until
 * somebody opened it - on a filter restored from a URL, the one moment the name
 * matters most.
 */
export function useSelectorRoster(): QueryLike<SelectorOption[]> {
  const { orgId, warehouseId } = useAssertedTenancyContext();
  const client = useMemo(() => apiClient(), []);

  return client.useQuery(
    "get",
    "/app_user/list",
    {
      params: {
        query: {
          org_id: orgId,
          warehouse_id: warehouseId,
          app_name: APP_NAME,
        },
      },
    },
    {
      select: (users) =>
        users
          // A deactivated selector cannot hold a task, so offering one here
          // would let a manager filter toward a name that can never match.
          .filter((user) => user.active)
          .map((user) => ({
            id: user.user_id,
            // AAA's `user_name` is optional - a selector created before it
            // existed, or without one set, falls back to the login id (same
            // fallback `displayNameOf` uses in `selectors.tsx`).
            label: user.user_name || user.user_id,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
    },
  );
}
